import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Element>} */
  #cachedSizeGuides = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /** De-dupes concurrent fetches for the same URL (e.g. hover prefetch + click).
   * @type {Map<string, Promise<Document | null>>} */
  #pendingFetches = new Map();

  /** Removes hover/focus prefetch listeners on disconnect. */
  #prefetchAbortController = new AbortController();

  /** Pending hover-intent timer; only a deliberate dwell triggers a prefetch.
   * @type {ReturnType<typeof setTimeout> | null} */
  #hoverIntentTimer = null;

  /**
   * The product template's namespaced main section id used for Section Rendering API
   * requests (e.g. "template--123__main"). Null until discovered from the first
   * full-page fetch, after which every card reuses it for the fast path. Shopify
   * 404s on the bare key "main", so we don't probe it. Static so discovery is shared
   * across all quick-add components.
   * @type {string | null}
   */
  static mainSectionId = null;

  /**
   * Gate that resolves once the first full-page fetch has discovered mainSectionId,
   * so concurrent prefetches wait for it instead of each fetching the full page.
   * @type {Promise<void> | null}
   */
  static sectionIdDiscovery = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    // If a sibling swatch (separate product) is selected, quick-view that product instead of the main card product.
    // productCardLink is never updated by sibling-swatches-component, so we check the checked input directly.
    if (productCard) {
      const siblingSwatches = productCard.querySelector('sibling-swatches-component');
      if (siblingSwatches instanceof HTMLElement) {
        const checkedSibling = siblingSwatches.querySelector('input[type="radio"]:checked');
        if (checkedSibling instanceof HTMLInputElement && checkedSibling.dataset.productUrl) {
          const checkedPath = new URL(checkedSibling.dataset.productUrl, window.location.origin).pathname;
          const mainPath = new URL(siblingSwatches.dataset.productUrl || productLink.href, window.location.origin).pathname;
          if (checkedPath !== mainPath) {
            return new URL(checkedSibling.dataset.productUrl, window.location.origin).toString();
          }
        }
      }
    }

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });

    // Warm the content cache when the user signals intent (hover dwell / keyboard
    // focus) so the first quick view opens instantly instead of waiting on the fetch.
    const target = this.closest('product-card') ?? this.closest('product-hotspot-component') ?? this;
    const { signal } = this.#prefetchAbortController;
    target.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    target.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    target.addEventListener('focusin', this.#prefetch, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#prefetchAbortController.abort();
    this.#clearHoverIntentTimer();
  }

  #handlePointerEnter = () => {
    // Touch devices fire pointerenter without true hover intent; the short delay also
    // prevents a mouse sweep across the grid from firing a fetch for every card.
    this.#clearHoverIntentTimer();
    this.#hoverIntentTimer = setTimeout(this.#prefetch, 120);
  };

  #handlePointerLeave = () => {
    this.#clearHoverIntentTimer();
  };

  #clearHoverIntentTimer() {
    if (this.#hoverIntentTimer !== null) {
      clearTimeout(this.#hoverIntentTimer);
      this.#hoverIntentTimer = null;
    }
  }

  /**
   * Best-effort cache warm-up. Errors are swallowed; the click path re-fetches if needed.
   */
  #prefetch = () => {
    this.#ensureCached(this.productPageUrl).catch(() => {});
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#cachedSizeGuides.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Cache is warmed on hover/focus, so this is usually a hit; falls back to fetch.
    const productGrid = await this.#ensureCached(currentUrl);
    const sizeGuideDrawer = this.#cachedSizeGuides.get(currentUrl);

    if (productGrid) {
      const handle = this.#extractHandleFromUrl(currentUrl);
      const dialogComponent = document.getElementById('quick-add-dialog');
      if (handle && dialogComponent) {
        dialogComponent.dataset.initialProductHandle = handle;
      }

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      const freshSizeGuide = sizeGuideDrawer
        ? /** @type {Element} */ (sizeGuideDrawer.cloneNode(true))
        : null;
      await this.updateQuickAddModal(freshContent, freshSizeGuide);
      this.#updateVariantPicker(productGrid);
    }
    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  /**
   * Extracts product handle from a product URL (e.g. /products/red-tshirt?variant=1 -> red-tshirt)
   * @param {string} url - Product page URL
   * @returns {string} Product handle or empty string
   */
  #extractHandleFromUrl(url) {
    if (!url) return '';
    try {
      const path = new URL(url, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/]+)/);
      return match ? match[1] : '';
    } catch {
      return '';
    }
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Ensures the product content for `url` is fetched and cached, returning the cached
   * product grid element (or null). Concurrent calls for the same URL share one
   * in-flight request, so a hover prefetch and a click never double-fetch.
   * @param {string} url - The product page URL
   * @returns {Promise<Element | null>}
   */
  async #ensureCached(url) {
    if (!url) return null;

    const cached = this.#cachedContent.get(url);
    if (cached) return cached;

    let pending = this.#pendingFetches.get(url);
    if (!pending) {
      pending = this.#fetchProductGridDocument(url);
      this.#pendingFetches.set(url, pending);
      pending.catch(() => {}).finally(() => this.#pendingFetches.delete(url));
    }

    const html = await pending;
    if (!html) return null;

    // Another caller may have populated the cache while we awaited; prefer that.
    const existing = this.#cachedContent.get(url);
    if (existing) return existing;

    const gridElement = html.querySelector('[data-product-grid-content]');
    /** @type {Element | null} */
    let productGrid = null;
    if (gridElement) {
      productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      copyProductDetailBeacon(gridElement.closest('[data-cnstrc-product-detail]'), productGrid);
      this.#cachedContent.set(url, productGrid);
    }
    const sizeGuideDrawer = html.querySelector('size-guide-drawer#size-guide-drawer');
    if (sizeGuideDrawer) {
      this.#cachedSizeGuides.set(url, /** @type {Element} */ (sizeGuideDrawer.cloneNode(true)));
    }
    return productGrid;
  }

  /**
   * Fetches the product content as a Document, preferring the Section Rendering API
   * (only the main section — roughly 90% smaller than the full page) and falling
   * back to a full-page fetch when section rendering is unavailable or doesn't
   * contain the product grid. The namespaced section id is discovered from the
   * fallback and cached so subsequent requests take the fast path.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async #fetchProductGridDocument(productPageUrl) {
    if (!productPageUrl) return null;

    // If the section id isn't known yet but discovery is already underway (e.g. a
    // concurrent hover prefetch on another card), wait for it so we can take the fast
    // path below instead of each caller redundantly fetching the full page.
    if (!QuickAddComponent.mainSectionId && QuickAddComponent.sectionIdDiscovery) {
      await QuickAddComponent.sectionIdDiscovery;
    }

    // 1. Once the namespaced section id is known, use the fast Section Rendering path.
    if (QuickAddComponent.mainSectionId) {
      try {
        const sectionDoc = await this.fetchProductPage(
          this.#withSectionId(productPageUrl, QuickAddComponent.mainSectionId)
        );
        if (sectionDoc?.querySelector('[data-product-grid-content]')) return sectionDoc;
      } catch {
        // Section rendering failed (e.g. a product on a different template). Fall through.
      }
    }

    // 2. Full-page fetch (first open, or section path failed). The first such call
    //    performs section-id discovery and publishes a gate so concurrent callers
    //    await it (above) rather than each fetching the full page.
    const startingDiscovery = !QuickAddComponent.mainSectionId && !QuickAddComponent.sectionIdDiscovery;
    /** @type {() => void} */
    let resolveDiscovery = () => {};
    if (startingDiscovery) {
      QuickAddComponent.sectionIdDiscovery = new Promise((resolve) => {
        resolveDiscovery = resolve;
      });
    }

    try {
      const fullDoc = await this.fetchProductPage(productPageUrl);
      if (fullDoc) this.#rememberMainSectionId(fullDoc);
      return fullDoc;
    } finally {
      if (startingDiscovery) {
        resolveDiscovery();
        QuickAddComponent.sectionIdDiscovery = null;
      }
    }
  }

  /**
   * Returns productPageUrl with the section_id query param set.
   * @param {string} productPageUrl - The base product page URL
   * @param {string} sectionId - The section id to request
   * @returns {string}
   */
  #withSectionId(productPageUrl, sectionId) {
    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  /**
   * Discovers the main section's namespaced id from a full-page document and caches
   * it for future Section Rendering API requests.
   * @param {Document} doc - The parsed full-page document
   */
  #rememberMainSectionId(doc) {
    const grid = doc.querySelector('[data-product-grid-content]');
    /** @type {Element | null} */
    let wrapper = grid;
    while (wrapper && !wrapper.id?.startsWith('shopify-section-')) wrapper = wrapper.parentElement;
    if (wrapper) QuickAddComponent.mainSectionId = wrapper.id.replace('shopify-section-', '');
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Applies quick-add layout transformations and morphs content into the modal.
   * Used by both QuickAddComponent and sibling-products (colour swatch switch).
   * @param {Element} productGrid - The product grid element from [data-product-grid-content]
   * @param {Object} options - Product context for transformations
   * @param {string} options.productTitle - Product title (for mobile header)
   * @param {string} options.productPageUrl - Product page URL (for links)
   * @param {Element | null} [options.sizeGuideDrawer] - Optional size guide drawer element
   */
  static applyQuickAddContent(productGrid, { productTitle, productPageUrl, sizeGuideDrawer = null }) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');

      const productTitleLink = document.createElement('a');
      productTitleLink.textContent = productTitle || '';
      productTitleLink.href = productPageUrl || '#';

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitleLink);

      if (productPrice) {
        const priceLinkWrapper = document.createElement('div');
        priceLinkWrapper.className = 'quick-add-price-link-wrapper';
        priceLinkWrapper.appendChild(productPrice);
        const link = document.createElement('a');
        link.className = 'quick-add-view-full-details';
        link.href = productPageUrl || '#';
        link.textContent =
          document.getElementById('quick-add-dialog')?.dataset.viewFullDetails || 'View more details';
        priceLinkWrapper.appendChild(link);
        productHeader.appendChild(priceLinkWrapper);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);
    // The morph is children-only, so the grid's own attributes never reach the
    // modal: carry the product-detail container across explicitly (PN-2236).
    copyProductDetailBeacon(productGrid, modalContent);

    if (!isMobileBreakpoint()) {
      const productPriceEl = modalContent.querySelector('product-price');
      if (productPriceEl) {
        const wrapper = document.createElement('div');
        wrapper.className = 'quick-add-price-link-wrapper';
        productPriceEl.parentNode?.insertBefore(wrapper, productPriceEl);
        wrapper.appendChild(productPriceEl);
        const link = document.createElement('a');
        link.className = 'quick-add-view-full-details';
        link.href = productPageUrl || '#';
        link.textContent =
          document.getElementById('quick-add-dialog')?.dataset.viewFullDetails || 'View more details';
        wrapper.appendChild(link);
      }
    }

    if (modalContent.querySelector('sibling-products') && !customElements.get('sibling-products')) {
      // sibling-products is a dynamic chunk not loaded on collection pages.
      // __loadSiblingProducts (exposed by _src/js/main/sibling-products/index.ts)
      // triggers Vite's dynamic import, which defines the custom element and
      // auto-upgrades any sibling-products elements already in the DOM.
      if (typeof window.__loadSiblingProducts === 'function') {
        window.__loadSiblingProducts();
      }

      // Buffer swatch clicks that arrive while the module is still loading.
      // Without this the first click is silently lost because the element is
      // still a plain HTMLElement with no handleSwatchClick handler.
      // Last click wins — only the most recent selection is replayed.
      let pendingSwatchInput = null;
      const siblingClickBuffer = (event) => {
        const input =
          event.target instanceof HTMLInputElement && event.target.dataset.handle
            ? event.target
            : null;
        if (!input) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        pendingSwatchInput = input;
      };
      modalContent.addEventListener('click', siblingClickBuffer, true);
      customElements.whenDefined('sibling-products').then(() => {
        modalContent.removeEventListener('click', siblingClickBuffer, true);
        if (pendingSwatchInput) {
          pendingSwatchInput.click();
          pendingSwatchInput = null;
        }
      });
    }

    if (sizeGuideDrawer) {
      const existing = modalContent.querySelector('size-guide-drawer#size-guide-drawer');
      if (existing) existing.remove();
      modalContent.appendChild(sizeGuideDrawer);
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {Element | null} [sizeGuideDrawer] - Optional size guide drawer element
   */
  async updateQuickAddModal(productGrid, sizeGuideDrawer = null) {
    QuickAddComponent.applyQuickAddContent(productGrid, {
      productTitle: this.dataset.productTitle || '',
      productPageUrl: this.productPageUrl,
      sizeGuideDrawer,
    });
    const modal = document.getElementById('quick-add-modal-content');
    if (modal) this.#syncVariantSelection(modal);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

/**
 * Copies Constructor's `data-cnstrc-*` product-detail container (PN-2236) from
 * one element to another, clearing the previous product's first. Quick View
 * lifts `[data-product-grid-content]` out of the product page, which leaves
 * behind the `.product-information` wrapper that carries the container on the
 * PDP. Consent refusal needs nothing here: `beacon-consent.ts` sweeps any
 * attribute that lands in the document.
 * @param {Element | null} from - Element carrying the container attributes
 * @param {Element} to - Element to stamp
 */
function copyProductDetailBeacon(from, to) {
  for (const name of to.getAttributeNames()) {
    if (name.startsWith('data-cnstrc-')) to.removeAttribute(name);
  }
  if (!from) return;
  for (const name of from.getAttributeNames()) {
    if (name.startsWith('data-cnstrc-')) to.setAttribute(name, from.getAttribute(name) ?? '');
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/** Time to show add-to-cart success state (`data-added`) before closing quick view */
const QUICK_ADD_SUCCESS_CLOSE_DELAY_MS = 1100;

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | null} */
  #pendingCloseAfterAddTimeout = null;

  /**
   * Prevents closing when click target is a nested dialog (e.g. colour overlay)
   * inside our modal — avoids quick-add closing when user clicks overlay backdrop.
   */
  shouldCloseOnOutsideClick(event) {
    const dialog = this.ownDialog;
    if (
      event.target instanceof HTMLDialogElement &&
      dialog?.contains(event.target) &&
      event.target !== dialog
    ) {
      return false;
    }
    return true;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#reorderSiblingSwatchesAfterVariantUpdate);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#clearPendingCloseAfterAdd();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  #clearPendingCloseAfterAdd() {
    if (this.#pendingCloseAfterAddTimeout !== null) {
      clearTimeout(this.#pendingCloseAfterAddTimeout);
      this.#pendingCloseAfterAddTimeout = null;
    }
  }

  /** Reset ATC success styling when the sheet/modal is dismissed */
  #clearModalAddToCartAddedState() {
    this.querySelector('#quick-add-modal-content')?.querySelectorAll('button[data-added]').forEach((btn) => {
      btn.removeAttribute('data-added');
    });
  }

  /**
   * Closes the dialog after a successful add so the CTA can show `data-added` first.
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    this.#clearPendingCloseAfterAdd();

    this.#pendingCloseAfterAddTimeout = setTimeout(() => {
      this.#pendingCloseAfterAddTimeout = null;
      this.closeDialog();
    }, QUICK_ADD_SUCCESS_CLOSE_DELAY_MS);
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const viewFullDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-view-full-details'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (viewFullDetailsLink) viewFullDetailsLink.href = anchorElement.href;
  };

  /**
   * Triggers sibling-products reorder after variant picker morph (variant option change).
   * Uses requestAnimationFrame so it runs after all sync handlers and DOM updates.
   */
  #reorderSiblingSwatchesAfterVariantUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    requestAnimationFrame(() => {
      const fn = window.__reorderQuickAddSiblingSwatches;
      if (typeof fn === 'function') fn();
    });
  };

  #handleDialogClose = () => {
    this.#clearPendingCloseAfterAdd();
    this.#clearModalAddToCartAddedState();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

// Expose for sibling-products (colour swatch switch in quick-add modal)
if (typeof window !== 'undefined') {
  /** @type {Window & { __applyQuickAddContent?: (grid: HTMLElement, opts: { productTitle: string; productPageUrl: string; sizeGuideDrawer?: Element | null }) => void }} */
  const win = window;
  win.__applyQuickAddContent = QuickAddComponent.applyQuickAddContent.bind(QuickAddComponent);
}
